import React from 'react';

import { useIntl } from 'react-intl';

import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useOnError } from 'src/utils/errors';
import { useDeleteReferenceRate } from 'src/features/referenceRates/api/useDeleteReferenceRate';
import { LIST_REFERENCE_RATES_RESOURCE } from 'src/features/referenceRates/api/useListReferenceRates';
import { referenceRatesMessages } from 'src/features/referenceRates/utils/messages';
import { ReferenceRate } from 'src/features/referenceRates/utils/types';

interface DeleteReferenceRateFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedReferenceRate: ReferenceRate;
    children?: React.ReactNode;
    onDeleted?: () => void;
}

function DeleteReferenceRateFormModal<TData>({
    selectedReferenceRate,
    children,
    onDeleted,
}: DeleteReferenceRateFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const onError = useOnError();

    const { mutate: deleteReferenceRate } = useDeleteReferenceRate({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_REFERENCE_RATES_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(referenceRatesMessages.deleteReferenceRateSuccess),
                description: formatMessage(referenceRatesMessages.deleteReferenceRateSuccessDescription),
            });
            onDeleted?.();
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...referenceRatesMessages.deleteReferenceRate },
                subTitle: {
                    ...referenceRatesMessages.deleteReferenceRateSubtitle,
                    values: { entity: selectedReferenceRate.name },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...referenceRatesMessages.confirmDeleteReferenceRate },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: referenceRatesMessages.deleteReferenceRate,
                callbackFn: () => deleteReferenceRate({ id: selectedReferenceRate.id }),
            }}
            children={children}
        />
    );
}

export default DeleteReferenceRateFormModal;
